import React, { useState, useEffect } from 'react';
import { Lock, Globe, Users, MessageCircle, Eye, EyeOff, Search, AtSign, Tag, Activity, Shield, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PrivacyOption = ({ icon: Icon, title, description, value, options, onChange, isToggle = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (isToggle) {
    return (
      <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700">
            <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <button
          onClick={() => onChange(!value)}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            value ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          data-testid={`toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              value ? 'left-7' : 'left-1'
            }`}
          />
        </button>
      </div>
    );
  }

  return (
    <div className="py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700">
            <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-primary font-medium capitalize">
            {options?.find(o => o.value === value)?.label || value}
          </span>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && options && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-3 ml-12 space-y-2 overflow-hidden"
          >
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => { onChange(option.value); setIsOpen(false); }}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                  value === option.value
                    ? 'bg-primary/10 border border-primary'
                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                data-testid={`option-${option.value}`}
              >
                <div className="flex items-center gap-2">
                  <option.icon className={`w-4 h-4 ${value === option.value ? 'text-primary' : 'text-gray-500'}`} />
                  <span className={`font-medium ${value === option.value ? 'text-primary' : ''}`}>{option.label}</span>
                </div>
                {value === option.value && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PrivacySettings = ({ user, onUpdate }) => {
  const [settings, setSettings] = useState({
    is_private: false,
    show_activity_status: true,
    allow_messages_from: 'everyone',
    show_posts_to: 'everyone',
    hide_followers_list: false,
    hide_following_list: false,
    hide_from_search: false,
    allow_tagging_from: 'everyone',
    allow_mentions_from: 'everyone'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/privacy-settings`, {
        withCredentials: true
      });
      setSettings(response.data.privacy_settings);
    } catch (error) {
      console.error('Failed to fetch privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/users/privacy-settings`, {
        [key]: value
      }, { withCredentials: true });
      
      toast.success('Privacy setting updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update setting');
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  const audienceOptions = [
    { value: 'everyone', label: 'Everyone', icon: Globe },
    { value: 'followers', label: 'Followers only', icon: Users },
    { value: 'none', label: 'No one', icon: Lock }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="privacy-settings">
      {/* Account Privacy */}
      <Card className="p-6 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Account Privacy</h3>
        </div>
        
        <PrivacyOption
          icon={Lock}
          title="Private Account"
          description="Only approved followers can see your posts"
          value={settings.is_private}
          onChange={(v) => updateSetting('is_private', v)}
          isToggle
        />
        
        <PrivacyOption
          icon={Search}
          title="Hide from Search"
          description="Your profile won't appear in search results"
          value={settings.hide_from_search}
          onChange={(v) => updateSetting('hide_from_search', v)}
          isToggle
        />
        
        <PrivacyOption
          icon={Activity}
          title="Activity Status"
          description="Show when you're active on Socyy"
          value={settings.show_activity_status}
          onChange={(v) => updateSetting('show_activity_status', v)}
          isToggle
        />
      </Card>

      {/* Interactions */}
      <Card className="p-6 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Interactions</h3>
        </div>
        
        <PrivacyOption
          icon={MessageCircle}
          title="Who can message you"
          description="Control who can send you direct messages"
          value={settings.allow_messages_from}
          options={audienceOptions}
          onChange={(v) => updateSetting('allow_messages_from', v)}
        />
        
        <PrivacyOption
          icon={AtSign}
          title="Who can mention you"
          description="Control who can @mention you in posts"
          value={settings.allow_mentions_from}
          options={audienceOptions}
          onChange={(v) => updateSetting('allow_mentions_from', v)}
        />
        
        <PrivacyOption
          icon={Tag}
          title="Who can tag you"
          description="Control who can tag you in photos"
          value={settings.allow_tagging_from}
          options={audienceOptions}
          onChange={(v) => updateSetting('allow_tagging_from', v)}
        />
      </Card>

      {/* Visibility */}
      <Card className="p-6 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Profile Visibility</h3>
        </div>
        
        <PrivacyOption
          icon={EyeOff}
          title="Hide Followers"
          description="Others can't see who follows you"
          value={settings.hide_followers_list}
          onChange={(v) => updateSetting('hide_followers_list', v)}
          isToggle
        />
        
        <PrivacyOption
          icon={EyeOff}
          title="Hide Following"
          description="Others can't see who you follow"
          value={settings.hide_following_list}
          onChange={(v) => updateSetting('hide_following_list', v)}
          isToggle
        />
      </Card>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          <strong>Note:</strong> When your account is private, only people you approve can see your posts, followers list, and following list. Your profile photo and bio will still be visible to everyone.
        </p>
      </div>
    </div>
  );
};

export default PrivacySettings;
